/**
 * Excel Utils for Earthquake
 * STRICT ALIGNMENT WITH WEB UI FORMATS & DEEP DATA EXTRACTION
 */

export const earthquakeMISConfig = {
    dist_date: { title: 'DIST_DATE_EARTHQUAKE_REPORT', dateColumn: true, blockColumn: true, dateFirst: false },
    dist: { title: 'DIST_EARTHQUAKE_REPORT', dateColumn: false, blockColumn: true, dateFirst: false },
    state: { title: 'STATE_EARTHQUAKE_REPORT', dateColumn: false, blockColumn: false, dateFirst: false },
    state_date: { title: 'STATE_DATE_EARTHQUAKE_REPORT', dateColumn: true, blockColumn: false, dateFirst: true }
};

export const EARTHQUAKE_MIS_FIELDS = [
    { api: 'no_of_deaths', label: 'Number of Deaths due to Earthquake (Today)', isCum: false },
    { api: 'no_of_deaths_cum', label: 'Number of Deaths due to Earthquake (Cumulative)', isCum: true },
    { api: 'no_of_injured_persons', label: 'Number of Injured Persons (Today)', isCum: false },
    { api: 'no_of_injured_persons_cum', label: 'Number of Injured Persons (Cumulative)', isCum: true },
    { api: 'no_of_houses_fully_damaged', label: 'Number of Houses Fully Damaged (Today)', isCum: false },
    { api: 'no_of_houses_fully_damaged_cum', label: 'Number of Houses Fully Damaged (Cumulative)', isCum: true },
    { api: 'no_of_houses_partially_damaged', label: 'Number of Houses Partially Damaged (Today)', isCum: false },
    { api: 'no_of_houses_partially_damaged_cum', label: 'Number of Houses Partially Damaged (Cumulative)', isCum: true },
    { api: 'no_of_people_rescued', label: 'Number of People Rescued (Today)', isCum: false },
    { api: 'no_of_people_rescued_cum', label: 'Number of People Rescued (Cumulative)', isCum: true },
    { api: 'no_of_relief_camps', label: 'Number of Relief Camps (Today)', isCum: false },
    { api: 'no_of_relief_camps_cum', label: 'Number of Relief Camps (Cumulative)', isCum: true },
    { api: 'remarks', label: 'Remarks', isCum: false, isText: true }
];

export const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return 'NA';
    if (typeof dateStr === 'number') {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    }
    if (typeof dateStr === 'string') {
        if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            const [d, m, y] = dateStr.split('/');
            return `${d}-${m}-${y}`;
        }
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [y, m, d] = dateStr.split('-');
            return `${d}-${m}-${y}`;
        }
    }
    return String(dateStr);
};

export const transformStatusDataForExcel = (data, columnOrder) => {
    if (!data || data.length === 0) return { columns: [], transformedData: [] };
    
    const columnMapping = {
      dist_name: "Name",
      total_block_count: "Total Blocks",
      report_submitted: "Report Submitted",
      report_not_submitted: "Report Not Submitted",
    };
    
    const columns = (columnOrder || ['dist_name', 'total_block_count', 'report_submitted', 'report_not_submitted']).map(key => ({
      label: columnMapping[key] || key.toUpperCase().replace(/_/g, " "),
      key: key,
    }));
    
    const transformedData = data.map(row => {
      const transformedRow = {};
      columns.forEach(col => { transformedRow[col.label] = row[col.key] ?? "0"; });
      return transformedRow;
    });
    return { columns, transformedData };
};

export const transformEarthquakeMISData = (rawData, viewType) => {
    const baseData = rawData.data || rawData; 
    const misData = baseData.mis_data || {};
    const cumDataObj = baseData.cumulative_data || {};

    const getValidArray = (...arrays) => arrays.find(arr => Array.isArray(arr)) || [];

    let dailyRecords = getValidArray(
        baseData.earthquake_report,
        misData.earthquake_report,
        baseData.heatwave,
        misData.heatwave,
        Array.isArray(baseData) ? baseData : null
    );

    let cumRecords = getValidArray(
        baseData.earthquake_report_cum,
        misData.earthquake_report_cum,
        baseData.heatwave_cum,
        baseData.heatwave_cumm,
        misData.heatwave_cum,
        misData.heatwave_cumm,
        cumDataObj.heatwave_cum,
        cumDataObj.heatwave_cumm,
        Array.isArray(cumDataObj) ? cumDataObj : null
    );

    // ✅ CRITICAL FIX: Ensure MIS generation works even when daily records are empty
    if (dailyRecords.length === 0 && cumRecords.length > 0) {
        dailyRecords = cumRecords.map(cum => ({
            region_id: cum.region_id || cum.dist_id || cum.id,
            block_name: cum.block_name || baseData?.block_name || 'NA',
            dist_name: cum.dist_name || baseData?.district_name || baseData?.district || 'NA',
            reported_on: cum.reported_on || baseData?.reported_on || baseData?.from_date,
            reported_on_iso: cum.reported_on_iso || baseData?.reported_on_iso
        }));
    }

    dailyRecords.sort((a, b) => {
        const dateA = new Date(a.reported_on_iso || a.reported_on || 0).getTime();
        const dateB = new Date(b.reported_on_iso || b.reported_on || 0).getTime();
        return dateA - dateB;
    });

    const config = earthquakeMISConfig[viewType] || earthquakeMISConfig.dist_date;
    
    const getId = (obj) => {
        const id = obj.dist_id || obj.region_id || obj.block_id || obj.id || 'NA';
        const date = obj.reported_on_iso || obj.reported_on || 'NO_DATE';
        return config.dateColumn ? `${id}_${date}` : id;
    };
    
    const cumMap = {};
    cumRecords.forEach(r => cumMap[getId(r)] = r);

    const headers = [];
    if (config.dateFirst) {
        if (config.dateColumn) headers.push('Date');
        headers.push('District Name');
    } else {
        headers.push('District Name');
        if (config.dateColumn) headers.push('Date');
    }
    if (config.blockColumn) headers.push('Block Name');
    
    EARTHQUAKE_MIS_FIELDS.forEach(field => headers.push(field.label));
    
    const rows = dailyRecords.map(rec => {
        const row = [];
        const cumRec = cumMap[getId(rec)] || {};
        const safeDate = formatDateForDisplay(rec.reported_on_iso || rec.reported_on || baseData.from_date);

        if (config.dateFirst) {
            if (config.dateColumn) row.push(safeDate);
            row.push(rec.dist_name || 'NA');
        } else {
            row.push(rec.dist_name || 'NA');
            if (config.dateColumn) row.push(safeDate);
        }
        if (config.blockColumn) row.push(rec.block_name || 'NA');

        EARTHQUAKE_MIS_FIELDS.forEach(field => {
            if (field.isText) {
                row.push(rec[field.api] || 'NA');
            } else if (field.isCum) {
                const val = cumRec[field.api];
                row.push((val !== null && val !== undefined) ? val : '-'); 
            } else {
                const val = rec[field.api];
                row.push((val !== null && val !== undefined) ? val : 0);
            }
        });
        return row;
    });

    if (rows.length > 0) {
        const totalRow = Array(headers.length).fill('');
        
        headers.forEach((colName, index) => {
            if (['Date', 'Block Name', 'Remarks'].includes(colName)) {
                totalRow[index] = 'NA';
            } else {
                let sum = 0;
                let hasValid = false;
                rows.forEach(r => {
                    const val = parseFloat(r[index]);
                    if (!isNaN(val)) {
                        sum += val;
                        hasValid = true;
                    }
                });
                totalRow[index] = hasValid ? (Number.isInteger(sum) ? sum : sum.toFixed(1)) : 'NA';
            }
        });

        if (config.dateFirst) {
            totalRow[0] = 'NA';
            totalRow[1] = 'TOTAL';
        } else {
            totalRow[0] = 'TOTAL';
            if (config.dateColumn) totalRow[1] = 'NA';
        }
        
        rows.push(totalRow);
    }
    
    return { headers, rows };
};